/**
 * Day maths of the event views, without a component.
 *
 * Every step between days goes through the `Date` constructor with a day
 * number, never through milliseconds: a day is 23 or 25 hours long across a
 * daylight-saving change, and `+ 86_400_000` would skip or repeat one.
 */
import { isIsoDate, parseIso, toIso } from '../Calendar/calendarGrid';
import type { NxCalendarEvent } from './types';

/** Local midnight of the day `date` lies in. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Local midnight `days` calendar days after `date`'s day. */
export function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** ISO `YYYY-MM-DD` of the local day `date` lies in — never UTC. */
export function toIsoDate(date: Date): string {
  return toIso(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Local midnight of an ISO day. An invalid value gives `null`. */
export function fromIsoDate(iso: string): Date | null {
  if (!isIsoDate(iso)) return null;
  const { year, month, day } = parseIso(iso);
  return new Date(year, month - 1, day);
}

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && Number.isFinite(value.getTime());
}

/** The entry has a start the views can place. */
export function hasValidStart(event: NxCalendarEvent): boolean {
  return isValidDate(event.start);
}

/**
 * Exclusive end in ms. A missing or earlier end is a point in time: one
 * millisecond past the start, so the entry still belongs to its start day.
 */
export function effectiveEndMs(event: NxCalendarEvent): number {
  const start = event.start.getTime();
  const end = isValidDate(event.end) ? event.end.getTime() : start;
  return Math.max(end, start + 1);
}

/**
 * The entry is over at `now`: its (exclusive) end has passed. A point in time
 * is over once its minute has begun. `event.past` overrides the clock.
 */
export function isEventPast(event: NxCalendarEvent, now: Date): boolean {
  if (typeof event.past === 'boolean') return event.past;
  if (!hasValidStart(event)) return false;
  return effectiveEndMs(event) <= now.getTime();
}

/** True when the entry covers any part of the local day starting at `dayStart`. */
export function overlapsDay(event: NxCalendarEvent, dayStart: Date): boolean {
  if (!hasValidStart(event)) return false;
  const from = dayStart.getTime();
  const to = addLocalDays(dayStart, 1).getTime();
  return event.start.getTime() < to && effectiveEndMs(event) > from;
}

/**
 * Reading order within a day: all-day entries first, then by start, then the
 * longer one first so a short entry never hides behind a long one.
 */
export function compareEvents(a: NxCalendarEvent, b: NxCalendarEvent): number {
  return (
    Number(Boolean(b.allDay)) - Number(Boolean(a.allDay)) ||
    a.start.getTime() - b.start.getTime() ||
    effectiveEndMs(b) - effectiveEndMs(a)
  );
}

/** The entries of one day, in reading order. */
export function eventsOnDay(events: readonly NxCalendarEvent[], dayStart: Date): NxCalendarEvent[] {
  return events.filter((event) => overlapsDay(event, dayStart)).sort(compareEvents);
}

export interface NxEventDay {
  /** ISO `YYYY-MM-DD`. */
  date: string;
  /** Local midnight. */
  day: Date;
  events: NxCalendarEvent[];
}

/**
 * The entries of a period grouped by local day, only days that have any:
 * the agenda. The range end is exclusive; an entry spanning several days is
 * listed on each of them, and an entry that began before the range shows up
 * on the range's first day.
 */
export function groupEventsByDay(
  events: readonly NxCalendarEvent[],
  range: { start: Date; end: Date }
): NxEventDay[] {
  const rangeStart = range.start.getTime();
  const rangeEnd = range.end.getTime();
  const days = new Map<string, NxEventDay>();
  for (const event of events) {
    if (!hasValidStart(event)) continue;
    const start = event.start.getTime();
    const end = effectiveEndMs(event);
    if (end <= rangeStart || start >= rangeEnd) continue;
    let day = startOfLocalDay(new Date(Math.max(start, rangeStart)));
    while (day.getTime() < end && day.getTime() < rangeEnd) {
      const key = toIsoDate(day);
      let entry = days.get(key);
      if (!entry) {
        entry = { date: key, day, events: [] };
        days.set(key, entry);
      }
      entry.events.push(event);
      day = addLocalDays(day, 1);
    }
  }
  return [...days.values()]
    .sort((a, b) => a.day.getTime() - b.day.getTime())
    .map((entry) => ({ ...entry, events: entry.events.sort(compareEvents) }));
}
